import type { ConnectorProject } from '@saaga/shared/interfaces/connector.interface'
import type { ReactNode } from 'react'
import { cn } from '@/lib/utils'

import { formatServiceType } from './connector-format.util'
import { ConnectorProjectRow } from './connector-project-row.atom'

export interface ConnectorProjectListProps {
  projectList: ConnectorProject[]
  connectionDisplayName: string
  onToggleActive: (projectId: string, isActive: boolean) => void
  className?: string
}

const groupProjectsByServiceType = (
  projectList: ConnectorProject[]
): Record<string, ConnectorProject[]> => {
  const groups: Record<string, ConnectorProject[]> = {}
  for (const project of projectList) {
    const group = groups[project.serviceType] ?? []
    group.push(project)
    groups[project.serviceType] = group
  }
  return groups
}

/**
 * Displays a grouped list of connector projects with toggle controls.
 * Projects are grouped by service type (e.g., Search Console, Analytics).
 */
export const ConnectorProjectList = ({
  projectList,
  connectionDisplayName,
  onToggleActive,
  className,
}: ConnectorProjectListProps): ReactNode => {
  const groupedProjectMap = groupProjectsByServiceType(projectList)
  const serviceTypeList = Object.keys(groupedProjectMap)

  if (projectList.length === 0) {
    return (
      <div className={cn('connector-project-list', className)}>
        <h3 className="text-base font-semibold">Connected Projects</h3>
        <p className="text-muted-foreground mt-1 text-sm">
          No projects discovered for {connectionDisplayName}.
        </p>
      </div>
    )
  }

  return (
    <div className={cn('connector-project-list space-y-6', className)}>
      <div>
        <h3 className="text-base font-semibold">Connected Projects</h3>
        <p className="text-muted-foreground mt-1 text-sm">
          Manage projects for {connectionDisplayName}. Active projects make their tools available to
          agents.
        </p>
      </div>
      {serviceTypeList.map((serviceType) => (
        <div key={serviceType} className="space-y-2">
          <h4 className="text-muted-foreground text-sm font-medium">
            {formatServiceType(serviceType)}
          </h4>
          <div className="space-y-2">
            {groupedProjectMap[serviceType].map((project) => (
              <ConnectorProjectRow
                key={project.id}
                project={project}
                onToggleActive={onToggleActive}
              />
            ))}
          </div>
        </div>
      ))}
    </div>
  )
}

ConnectorProjectList.displayName = 'ConnectorProjectList'
